import React, { useCallback, useContext, useEffect, useState } from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    AppBar,
    Toolbar,
    IconButton,
    Button,
    Grid,
    Box,
    Tabs,
    Tab,
    Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import SaveIcon from '@mui/icons-material/Save'
import CancelIcon from '@mui/icons-material/Cancel'
import { Close } from 'genesis-suite/icons'
import CompanyDetails from './CompanyDetails'
import DomainDetails from './DomainDetails'
import makeStyles from '@mui/styles/makeStyles'
import { useForm } from 'react-hook-form'
import { domainService } from '../../lib/services'
import { useSnackbar } from 'notistack'
import { DimLoader, SwalContext } from 'genesis-suite/components'
import { v1 as uuid } from 'uuid'
import axios from 'axios'
import usePreventClose from 'genesis-suite/hooks/useBeforeUnload'

const useStyles = makeStyles(({ spacing }) => ({
    closeButton: {
        position: 'absolute',
        top: spacing(1),
        right: spacing(1),
    },
}))

const geoBaseUrl = 'https://tadacdn.tadanow.com/geo/'

const initialValue = {
    address: {
        line1: '',
        line2: '',
        line3: '',
        cityName: '',
        provinceName: '',
        postalCode: '',
        countryName: '',
        countryCode: '',
    },
    domains: [],
    url: '',
    logoUrl: null,
    isAuthority: false,
    adminEmailAddress: '',
    id: null,
    name: '',
}
const ManageCompaniesModal = ({
    open,
    onClose,
    selectedCompany,
    setSelectedCompany,
    hasEditCompany,
    setHasEditCompany,
    gridAPI,
}) => {
    const classes = useStyles()
    const [isNewCompany, setIsNewCompany] = useState(false)
    const [tab, setTab] = useState(0)
    const [selectedDomain, setSelectedDomain] = useState(null)
    const [originalValues, setOriginalValues] = useState(null)
    const [hasDomainChanges, setHasDomainChanges] = useState(false)
    const [loading, setLoading] = useState(false)
    const { enqueueSnackbar: showSnackbar } = useSnackbar()
    const [locationData, setLocationData] = useState({ countries: [], states: [], cities: [] })

    const { alert, confirm } = useContext(SwalContext)

    const [domainState, setDomainState] = useState({
        isAddingSubdomain: false,
        selectedNodeId: null,
        parentDomain: null,
        isAddingDomain: false,
        expandedNodes: [],
    })

    const errCallback = useCallback(
        err => {
            alert(err.Message || err.message, { type: 'error' })
            setLoading(false)
        },
        [alert]
    )
    const {
        handleSubmit,
        control,
        reset,
        getValues,
        formState: { isDirty, isValid, errors },
        watch,
        trigger,
        setValue,
    } = useForm({
        mode: 'onChange',
        defaultValues: initialValue,
    })
    useEffect(() => {
        if (selectedCompany && hasEditCompany) {
            reset(selectedCompany)
            setOriginalValues(selectedCompany)
            const { address } = selectedCompany
            setValue('address.countryName', address?.countryName || '')
            setValue('address.provinceName', address?.provinceName || '')
            setValue('address.cityName', address?.cityName || '')
        }
        // That's use for when click on add new company form grid
        else if (!hasEditCompany) {
            setIsNewCompany(true)
        }
    }, [selectedCompany, hasEditCompany])
    useEffect(() => {
        const fetchGeoInfo = async () => {
            try {
                const [countriesRes, statesRes, citiesRes] = await Promise.all([
                    axios.get(`${geoBaseUrl}allcountries.json`),
                    axios.get(`${geoBaseUrl}allstates.json`),
                    axios.get(`${geoBaseUrl}allcities.json`),
                ])
                setLocationData({
                    countries: countriesRes.data,
                    states: statesRes.data,
                    cities: citiesRes.data,
                })
            } catch (error) {
                console.error('Error fetching geo data:', error)
            }
        }

        fetchGeoInfo()
    }, [])

    const handleCompanyClick = () => {
        setIsNewCompany(false)
        setTab(0)
        setSelectedDomain(null)
        setHasDomainChanges(false)
    }

    const handleAddNewCompany = () => {
        setSelectedDomain(null)
        setIsNewCompany(true)
        setTab(0)
        reset(initialValue)
        setHasDomainChanges(false)
    }
    const refreshCell = (key, response) => {
        gridAPI.applyServerSideTransaction({ [key]: [response] })
        gridAPI?.refreshCells()
    }
    const doSave = async data => {
        try {
            let response
            setLoading(true)
            if (data?.id) {
                response = await domainService.updateCompany(data)
                refreshCell('update', response)
            } else {
                response = await domainService.saveCompany(data)
                refreshCell('add', response)
                setHasEditCompany(true)
            }
            setSelectedCompany(response)
            setIsNewCompany(false)
            setHasDomainChanges(false)
            showSnackbar('Save successful', { variant: 'success' })
        } catch (error: any) {
            if (error?.__CANCEL__) return
            errCallback(error)
        } finally {
            setLoading(false)
        }
    }
    const onSubmit = async data => {
        await doSave(data)
    }
    const watchedValues = watch()
    const hasChanges = JSON.stringify(originalValues) !== JSON.stringify(watchedValues)

    const handleAddDomainData = domainData => {
        const currentDomains = getValues('domains') || []
        // const currentDomains = selectedCompany.domains || [];
        let updatedDomains = [...currentDomains] as any

        if (domainData.isSubdomain && domainData.parentDomain) {
            const parentDomainIndex = updatedDomains.findIndex(domain => domain.id === domainData.parentDomain.id)

            if (parentDomainIndex > -1) {
                const parentDomain = { ...updatedDomains[parentDomainIndex] }

                const updatedSubDomains = [...(parentDomain.subDomains || [])]

                const name = domainData.name

                const subDomainIndex = updatedSubDomains.findIndex(subDomain => subDomain.id === domainData.id)
                if (subDomainIndex > -1) {
                    updatedSubDomains[subDomainIndex] = {
                        ...updatedSubDomains[subDomainIndex],
                        name: name || updatedSubDomains[subDomainIndex].name,
                        friendlyName: domainData.friendlyName || updatedSubDomains[subDomainIndex].friendlyName,
                        adminEmailAddress:
                            domainData.adminEmailAddress || updatedSubDomains[subDomainIndex].adminEmailAddress,
                        isSubdomain: true,
                    }
                } else {
                    updatedSubDomains.push({
                        name: name,
                        friendlyName: domainData.friendlyName,
                        adminEmailAddress: domainData.adminEmailAddress,
                        id: domainData.id || uuid(),
                        isRoot: false,
                        isSubdomain: true,
                    })
                }

                parentDomain.subDomains = updatedSubDomains
                updatedDomains[parentDomainIndex] = parentDomain
            }
        } else {
            const existingDomainIndex = updatedDomains.findIndex(domain => domain.id === domainData.id)
            if (existingDomainIndex > -1) {
                updatedDomains[existingDomainIndex] = {
                    ...updatedDomains[existingDomainIndex],
                    name: domainData.name || updatedDomains[existingDomainIndex].name,
                    friendlyName: domainData.friendlyName || updatedDomains[existingDomainIndex].friendlyName,
                    adminEmailAddress:
                        domainData.adminEmailAddress || updatedDomains[existingDomainIndex].adminEmailAddress,
                    isDomain: true,
                }
            } else {
                updatedDomains = [
                    ...updatedDomains,
                    {
                        name: domainData.name,
                        friendlyName: domainData.friendlyName,
                        adminEmailAddress: domainData.adminEmailAddress,
                        subDomains: [],
                        id: domainData.id || uuid(),
                        isDomain: true,
                    },
                ]
            }
        }

        setValue('domains', updatedDomains, { shouldDirty: true, shouldValidate: true })
        setSelectedCompany(prevCompany => ({
            ...prevCompany,
            domains: updatedDomains,
        }))
        setHasDomainChanges(true)
        return updatedDomains
    }
    const handleDeleteDomain = updatedCompany => {
        const safeDomains = Array.isArray(updatedCompany.domains) ? updatedCompany.domains : []
        setValue('domains', safeDomains, { shouldDirty: true, shouldValidate: true })
        setSelectedCompany(prevCompany => ({
            ...prevCompany,
            domains: safeDomains,
        }))

        setHasDomainChanges(true)
    }

    const handleClose = () => {
        onClose()
        handleCompanyClick()
        setDomainState(null)
    }

    usePreventClose(isDirty || hasDomainChanges || !isValid)
    const handleCancel = async () => {
        if (isDirty || hasDomainChanges || !isValid) {
            const response = await confirm('You have unsaved changes and you will lose them. Do you want to proceed?', {
                type: 'question',
            })
            if (response.dismiss) return
            handleClose()
            gridAPI?.deselectAll()
            gridAPI?.refreshCells()
        } else {
            handleClose()
            gridAPI?.deselectAll()
            gridAPI?.refreshCells()
        }
    }

    return (
        <React.Fragment>
            {loading && <DimLoader />}
            <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
                <DialogTitle>{`${hasEditCompany ? 'Edit' : 'Add'} Company`}</DialogTitle>
                <AppBar position="sticky" color="default" elevation={0}>
                    <Toolbar>
                        {(selectedCompany || isNewCompany) && (
                            <div style={{ marginLeft: 'auto' }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<SaveIcon />}
                                    // disabled={!isDirty || !hasChanges || !isValid || !hasDomainChanges}
                                    disabled={!(isDirty || hasDomainChanges) || !isValid}
                                    onClick={handleSubmit(onSubmit)}
                                >
                                    Save
                                </Button>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    startIcon={<CancelIcon />}
                                    style={{ marginLeft: '10px' }}
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </Button>
                            </div>
                        )}
                    </Toolbar>
                </AppBar>
                <DialogContent style={{ padding: 0, height: '70vh' }}>
                    <IconButton className={classes.closeButton} onClick={handleClose} size="large">
                        <Close />
                    </IconButton>
                    <Grid container spacing={0} style={{ height: '100%' }}>
                        {/* Left side: Company List */}
                        {/* <CompanyList companies={companies} handleCompanyClick={handleCompanyClick} /> */}

                        {/* Right side: Company/Domain Details */}
                        <Grid
                            item
                            xs={12}
                            style={{ padding: '20px', display: 'flex', flexDirection: 'column', height: '100%' }}
                        >
                            {!selectedCompany && !isNewCompany ? (
                                <Typography align="center" style={{ marginTop: '20%', color: '#7b7b7b' }}>
                                    Select a company from the list on the left or add a new company by clicking the{' '}
                                    <AddIcon /> icon.
                                </Typography>
                            ) : tab === 0 ? (
                                <CompanyDetails
                                    selectedCompany={selectedCompany}
                                    isNewCompany={isNewCompany}
                                    control={control}
                                    errors={errors}
                                    getValues={getValues}
                                    watch={watch}
                                    trigger={trigger}
                                    setValue={setValue}
                                    locationData={locationData}
                                />
                            ) : isNewCompany ? (
                                <Typography
                                    variant="h6"
                                    sx={{
                                        height: '100%',
                                        textAlign: 'center',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                    }}
                                >
                                    New company must be saved before adding domains.
                                </Typography>
                            ) : (
                                <DomainDetails
                                    selectedDomain={selectedDomain}
                                    setSelectedDomain={setSelectedDomain}
                                    selectedCompany={selectedCompany}
                                    onSave={handleAddDomainData}
                                    onDelete={handleDeleteDomain}
                                    domainState={domainState}
                                    setDomainState={setDomainState}
                                />
                            )}

                            {/* Sticky Tabs */}
                            {(selectedCompany || isNewCompany || selectedDomain) && (
                                <Box
                                    position="sticky"
                                    bottom={0}
                                    bgcolor="white"
                                    style={{ borderTop: '1px solid #ddd' }}
                                >
                                    <Tabs value={tab} onChange={(e, newValue) => setTab(newValue)} variant="standard">
                                        <Tab label="Information" />
                                        <Tab label="Domains" />
                                    </Tabs>
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>
        </React.Fragment>
    )
}

export default ManageCompaniesModal
